import { Component, computed, contentChild, ElementRef, HostBinding, input, Signal, signal, viewChild } from '@angular/core';
import { ThemeColor, ThemeSize } from '../helpers';

type ButtonFormFactor = 'square' | 'default';

type ButtonFill = 'filled' | 'clear' | 'outline';

type ButtonState = 'default' | 'loading';

type ButtonExpand = 'default' | 'block';

@Component({
  selector: 'app-pan-button, [panButton]',
  templateUrl: './pan-button.component.html',
  styleUrl: './pan-button.component.scss',
  standalone: false,
})
export class PanButtonComponent {
  public color = input<ThemeColor>('default');
  public ariaLabel = input<string>('');
  public disabled = input<boolean>(false);
  public expand = input<ButtonExpand>('default');
  public fill = input<ButtonFill>('filled');
  public form = input<ButtonFormFactor>('default');
  public icon = input<string>('');
  public label = input<string>('');
  public size = input<ThemeSize>('md');
  public state = input<ButtonState>('default');
  public type = input<string>('');
  public width = input<number>();

  // used for panRadio and panCheckbox
  public overrideFill = signal<ButtonFill | undefined>(undefined);
  public overrideRole = signal<string | null>(null);
  public overrideAriaChecked = signal<string | null>(null);
  public overrideAriaPressed = signal<string | null>(null);
  public fillValue = computed(() => this.overrideFill() ?? this.fill());

  public menuChild: Signal<ElementRef> = contentChild('menu');
  public labelTextElement = viewChild<ElementRef<HTMLElement>>('labelText');
  public hasMenu = computed(() => !!this.menuChild());

  public classes = computed(() => {
    return [
      'btn',
      `btn-color-${this.color()}`,
      `btn-form-${this.form()}`,
      `btn-fill-${this.fillValue()}`,
      `btn-state-${this.state()}`,
      `btn-size-${this.size()}`,
      `btn-expand-${this.expand()}`,
      this.disabled() ? 'btn-disabled' : '',
      this.hasMenu() ? 'btn-has-menu' : '',
      this.isMenuOnly() ? 'btn-menu-only' : '',
    ].filter(Boolean);
  });

  public isMenuOnly = computed(() => this.hasMenu() && !this.label() && !this.icon());

  @HostBinding('class.disabled')
  public get isDisabled() {
    return this.disabled();
  }

  @HostBinding('class.expand-block')
  public get isExpand() {
    return this.expand() === 'block';
  }

  public buttonAriaLabel() {
    if (this.state() === 'loading') {
      return 'loading';
    }

    const explicitAriaLabel = this.ariaLabel().trim();
    if (explicitAriaLabel) {
      return explicitAriaLabel;
    }

    const labelInput = this.label().trim();
    if (labelInput) {
      return labelInput;
    }

    const projectedLabelText = this.labelTextElement()?.nativeElement.textContent?.trim();
    return projectedLabelText || null;
  }
}
